import { useCallback, useEffect, useMemo, useState } from "react"
import { Alert, Box, Button, CircularProgress, Tooltip, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { EventRepeatOutlined, WarningAmberOutlined } from "@mui/icons-material"
import { DataGridPremium } from "@mui/x-data-grid-premium"
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium"
import { visuallyHidden } from "@mui/utils"
import { useRefetchScheduledWorkflowsAt, useScheduledWorkflows } from "resources/scheduled-workflows/hooks"
import type { ScheduledWorkflowListItem } from "resources/scheduled-workflows/types"
import { ToolbarWithSearch } from "../chatbox/toolbar-with-search"
import { FilterPill, SURFACE_HEADER_SX, SURFACE_PILLS_SX, surfaceGridBaseSx, surfaceRootSx } from "../chatbox/surface-styles"
import { formatReportTime, formatSchedule, nextBudgetReset, scheduleStatusLine, viewerTimeZone } from "./schedule-format"
import { RUN_OUTCOME_DISPLAY, failedTooltip, latestReportLabel, latestReportOf } from "./latest-report"
import { WorkflowReportView } from "./report-panel"
import { ReconnectButton, RecoverButton, RowActions } from "./row-actions"

const SURFACE_CONTAINER = "kelly-workflows-surface"
const ROOT_SX = surfaceRootSx(SURFACE_CONTAINER)
const GRID_SX = ((theme: Theme) => ({
  ...surfaceGridBaseSx(theme, SURFACE_CONTAINER),
  "& .MuiDataGrid-columnSeparator": { display: "none" },
  "& .MuiDataGrid-row:hover": { backgroundColor: "action.hover" },
  "& .MuiDataGrid-cell:focus": { outline: "none" },
  "& .MuiDataGrid-cell": { display: "flex", flexDirection: "column", justifyContent: "center" },
  "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": { py: 1 },
})) satisfies SxProps<Theme>
const CENTER_SX = { display: "flex", justifyContent: "center", p: 3 } satisfies SxProps<Theme>
const EMPTY_SX = { display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 0.5, px: 3, py: 6 } satisfies SxProps<Theme>
const EMPTY_GLYPH_SX = { fontSize: "2.5rem", mb: 1, color: "text.disabled" } satisfies SxProps<Theme>
const BUDGET_RESET_SETTLE_MILLISECONDS = 5_000
const BUDGET_RESET_RETRY_WINDOW_MILLISECONDS = 60_000
const BUDGET_RESET_RETRY_MILLISECONDS = 15_000
const ELLIPSIS_SX = { maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } satisfies SxProps<Theme>
const NAME_SX = { ...ELLIPSIS_SX, fontWeight: "bold" } satisfies SxProps<Theme>
const SECONDARY_LINE_SX = { ...ELLIPSIS_SX, color: "text.secondary" } satisfies SxProps<Theme>
const BLOCKED_LINE_SX = { display: "flex", alignItems: "center", gap: 0.5 } satisfies SxProps<Theme>
const BLOCKED_ICON_SX = { fontSize: "1rem", color: "warning.main", flexShrink: 0 } satisfies SxProps<Theme>
const REPORT_SX = { display: "flex", alignItems: "center", gap: 0.75 } satisfies SxProps<Theme>
const REPORT_BUTTON_SX = { p: 0, minWidth: 0, textTransform: "none" } satisfies SxProps<Theme>
const NAME_BUTTON_SX = { p: 0, minWidth: 0, textTransform: "none", justifyContent: "flex-start", maxWidth: "100%", color: "text.primary" } satisfies SxProps<Theme>

const FILTERS = [
  { id: "active", label: "Active" },
  { id: "archived", label: "Archived" },
] as const

type WorkflowFilter = typeof FILTERS[number]["id"]

const EMPTY_COPY: Record<WorkflowFilter, string> = {
  active: "Ask Kelly to set up a workflow.",
  archived: "No archived workflows.",
}

function WorkflowsToolbar() {
  return <ToolbarWithSearch placeholder="Search workflows…" ariaLabel="Search workflows" />
}

interface LatestReportCellProps {
  workflow: ScheduledWorkflowListItem
  now: Date
  onOpenReport?: (deliverableId: string) => void
}

function LatestReportCell({ workflow, now, onOpenReport }: LatestReportCellProps) {
  const report = latestReportOf(workflow.lastRun, workflow.latestDeliverable)
  const { color, icon: Icon } = RUN_OUTCOME_DISPLAY[report.state]
  const label = latestReportLabel(report, (time) => formatReportTime(time, now))
  const deliverable = workflow.latestDeliverable
  const content = (
    <Box sx={REPORT_SX} tabIndex={report.state === "failed" ? 0 : undefined}>
      {report.state === "running" ? <CircularProgress size={14} color="info" /> : <Icon fontSize="small" sx={{ color }} />}
      {report.state === "report" && onOpenReport != null && deliverable != null
        ? <Button size="small" variant="text" sx={REPORT_BUTTON_SX} onClick={(event) => { event.currentTarget.focus(); onOpenReport(deliverable.deliverableId) }}>{label}</Button>
        : <Typography variant="body2">{label}</Typography>}
    </Box>
  )
  if (report.state !== "failed") return content
  return <Tooltip title={failedTooltip(workflow.lastRun?.errorType ?? null)}>{content}</Tooltip>
}

export function ScheduledWorkflowsSurface({ onOpenReport }: { onOpenReport: (deliverableId: string) => void }) {
  const [filter, setFilter] = useState<WorkflowFilter>("active")
  const { workflows, dataUpdatedAt, isLoading, isError, isIncomplete } = useScheduledWorkflows({ includeArchived: filter === "archived" })
  const [now, setNow] = useState(() => new Date())
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null)

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(timer)
  }, [])

  const handleBack = useCallback(() => setSelectedWorkflowId(null), [])

  const listedAt = useMemo(() => new Date(dataUpdatedAt), [dataUpdatedAt])
  const hasOverBudgetRow = workflows?.some((workflow) => workflow.blockedReason === "over_budget") ?? false
  const isListedJustAfterReset = listedAt.getTime() - Date.UTC(listedAt.getUTCFullYear(), listedAt.getUTCMonth(), listedAt.getUTCDate()) < BUDGET_RESET_RETRY_WINDOW_MILLISECONDS
  const budgetRefetchAt = isListedJustAfterReset ? listedAt.getTime() + BUDGET_RESET_RETRY_MILLISECONDS : nextBudgetReset(listedAt).getTime() + BUDGET_RESET_SETTLE_MILLISECONDS
  useRefetchScheduledWorkflowsAt(hasOverBudgetRow ? budgetRefetchAt : null)

  const rows = useMemo(
    () => (workflows ?? []).filter((workflow) => (filter === "archived") === (workflow.archivedAt != null)),
    [workflows, filter],
  )

  const columns = useMemo<GridColDef<ScheduledWorkflowListItem>[]>(() => [
    {
      field: "name",
      headerName: "Workflow",
      flex: 1,
      minWidth: 180,
      valueGetter: (_value, row) => `${row.name} ${row.description ?? ""}`,
      renderCell: ({ row }: GridRenderCellParams<ScheduledWorkflowListItem>) => (
        <>
          <Button size="small" variant="text" sx={NAME_BUTTON_SX} onClick={() => setSelectedWorkflowId(row.scheduledWorkflowId)}>
            <Typography variant="body2" sx={NAME_SX} title={row.name}>{row.name}</Typography>
          </Button>
          {row.description != null && <Typography variant="caption" sx={SECONDARY_LINE_SX} title={row.description}>{row.description}</Typography>}
        </>
      ),
    },
    {
      field: "schedule",
      headerName: "Schedule",
      flex: 1,
      minWidth: 160,
      sortable: false,
      getApplyQuickFilterFn: () => null,
      renderCell: ({ row }: GridRenderCellParams<ScheduledWorkflowListItem>) => {
        const statusLine = scheduleStatusLine(row, now, viewerTimeZone(), listedAt)
        const statusText = <Typography variant="caption" sx={SECONDARY_LINE_SX} title={statusLine ?? undefined}>{statusLine}</Typography>
        return (
          <>
            {row.schedule != null && <Typography variant="body2" sx={ELLIPSIS_SX}>{formatSchedule(row.schedule, viewerTimeZone(), { now, nextRunAt: row.nextRunAt })}</Typography>}
            {row.blockedReason != null ? <Box sx={BLOCKED_LINE_SX}><WarningAmberOutlined sx={BLOCKED_ICON_SX} />{statusText}</Box> : statusText}
            <ReconnectButton workflow={row} />
            <RecoverButton workflow={row} />
          </>
        )
      },
    },
    {
      field: "latestReport",
      headerName: "Latest report",
      flex: 0.8,
      minWidth: 150,
      getApplyQuickFilterFn: () => null,
      valueGetter: (_value, row) => {
        const { time } = latestReportOf(row.lastRun, row.latestDeliverable)
        return time == null ? Number.NEGATIVE_INFINITY : new Date(time).getTime()
      },
      renderCell: ({ row }: GridRenderCellParams<ScheduledWorkflowListItem>) => <LatestReportCell workflow={row} now={now} onOpenReport={onOpenReport} />,
    },
    {
      field: "actions",
      headerName: "Actions",
      renderHeader: () => <Box component="span" sx={visuallyHidden}>Actions</Box>,
      width: 136,
      sortable: false,
      align: "right",
      renderCell: ({ row }: GridRenderCellParams<ScheduledWorkflowListItem>) => <RowActions workflow={row} />,
    },
  ], [now, listedAt, onOpenReport])

  const selectedWorkflow = selectedWorkflowId == null ? undefined : workflows?.find((workflow) => workflow.scheduledWorkflowId === selectedWorkflowId)
  if (selectedWorkflow != null) {
    return (
      <Box sx={ROOT_SX} data-testid="scheduled-workflows-surface">
        <WorkflowReportView workflow={selectedWorkflow} onBack={handleBack} onOpenReport={onOpenReport} />
      </Box>
    )
  }

  return (
    <Box sx={ROOT_SX} data-testid="scheduled-workflows-surface">
      <Box sx={SURFACE_HEADER_SX}>
        <Typography variant="h6" component="h2">Scheduled Workflows</Typography>
      </Box>
      {isError && workflows == null && <Alert severity="error" data-testid="scheduled-workflows-error">Couldn&apos;t load your workflows. Please try again later.</Alert>}
      {isIncomplete && workflows != null && <Alert severity="warning" data-testid="scheduled-workflows-incomplete">Couldn&apos;t load all your workflows. Please try again later.</Alert>}
      <Box sx={SURFACE_PILLS_SX} role="group" aria-label="Filter workflows">
        {FILTERS.map(({ id, label }) => (
          <FilterPill
            key={id}
            label={label}
            size="small"
            variant="outlined"
            isSelected={filter === id}
            onClick={() => { setFilter(id); setSelectedWorkflowId(null) }}
            aria-pressed={filter === id}
            data-testid={`scheduled-workflows-filter-${id}`}
          />
        ))}
      </Box>

      {isLoading && rows.length === 0 && (
        <Box sx={CENTER_SX} data-testid="scheduled-workflows-loading">
          <CircularProgress size={20} aria-label="Loading workflows" />
        </Box>
      )}
      {!isLoading && workflows != null && rows.length === 0 && (
        <Box sx={EMPTY_SX} data-testid="scheduled-workflows-empty">
          <EventRepeatOutlined sx={EMPTY_GLYPH_SX} />
          <Typography variant="body2" color="textSecondary">{EMPTY_COPY[filter]}</Typography>
        </Box>
      )}
      {rows.length > 0 && (
        <DataGridPremium
          rows={rows}
          columns={columns}
          getRowId={(row) => row.scheduledWorkflowId}
          getRowHeight={() => "auto"}
          density="compact"
          disableRowSelectionOnClick
          onRowClick={({ row }, event) => {
            if (!(event.target instanceof Element) || !event.currentTarget.contains(event.target) || event.target.closest("button, a, input, [role='menuitem'], [role='switch']") != null) return
            setSelectedWorkflowId(row.scheduledWorkflowId)
          }}
          disableColumnMenu
          disableColumnResize
          hideFooter
          sx={GRID_SX}
          slots={{ toolbar: WorkflowsToolbar }}
          showToolbar
          initialState={{ sorting: { sortModel: [{ field: "latestReport", sort: "desc" }] } }}
          data-testid="scheduled-workflows-grid"
        />
      )}
    </Box>
  )
}
